import { css } from 'styled-components';
import { useState, useEffect } from 'react';

/**
 * @deprecated no longer recommended, see LUI-877, LUI-910, and LUI-875 for more details
 */
var cssResetObj = {
  all: 'initial',
  boxSizing: 'border-box'
};

/**
 * @deprecated no longer recommended, see LUI-877, LUI-910, and LUI-875 for more details
 */
var cssReset = css(["all:initial;box-sizing:border-box;"]);

/**
 * Escape regex char
 *
 * See: https://stackoverflow.com/questions/17885855/use-dynamic-variable-string-as-regex-pattern-in-javascript
 */
var escapeRegExp = stringToGoIntoTheRegex => {
  // eslint-disable-next-line no-useless-escape
  return stringToGoIntoTheRegex.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
};

// Global whitelist props for all LUI components
var whitelist = ['id', 'data-component', 'data-testid', 'data-tracking-event'];
// Global props should always apply to the outermost component
var filterGlobalProps = props => {
  return Object.keys(props).filter(propKey => whitelist.includes(propKey)).reduce((obj, propKey) => {
    obj[propKey] = props[propKey];
    return obj;
  }, {});
};

/**
 * note: useLocalStorage is being transitioned to sessionStorage
 */
function useLocalStorage(key, initialValue) {
  // Get from local storage then
  // parse stored json or return initialValue
  var readValue = () => {
    // Prevent build error "window is undefined" but keep keep working
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      var item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn("Error reading sessionStorage key \u201C".concat(key, "\u201D:"), error);
      return initialValue;
    }
  };

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  var [storedValue, setStoredValue] = useState(readValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to sessionStorage.
  var setValue = value => {
    // Prevent build error "window is undefined" but keeps working
    if (typeof window === 'undefined') {
      console.warn("Tried setting sessionStorage key \u201C".concat(key, "\u201D even though environment is not a client"));
    }
    try {
      // Allow value to be a function so we have the same API as useState
      var newValue = value instanceof Function ? value(storedValue) : value;

      // Save to local storage
      window.sessionStorage.setItem(key, JSON.stringify(newValue));

      // Save state
      setStoredValue(newValue);

      // We dispatch a custom event so every useLocalStorage hook are notified
      window.dispatchEvent(new Event('local-storage'));
    } catch (error) {
      console.warn("Error setting sessionStorage key \u201C".concat(key, "\u201D:"), error);
    }
  };
  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    var handleStorageChange = () => {
      setStoredValue(readValue());
    };

    // this only works for other documents, not the current one
    window.addEventListener('storage', handleStorageChange);

    // this is a custom event, triggered in writeValueToLocalStorage
    window.addEventListener('local-storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('local-storage', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [storedValue, setValue];
}
function useReadLocalStorage(key) {
  // Get from local storage then
  // parse stored json or return initialValue
  var readValue = () => {
    // Prevent build error "window is undefined" but keep keep working
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      var item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.warn("Error reading sessionStorage key \u201C".concat(key, "\u201D:"), error);
      return null;
    }
  };

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  var [storedValue, setStoredValue] = useState(readValue);

  // Listen if sessionStorage changes
  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    var handleStorageChange = () => {
      setStoredValue(readValue());
    };

    // this only works for other documents, not the current one
    window.addEventListener('storage', handleStorageChange);

    // this is a custom event, triggered in writeValueToLocalStorage
    // See: useLocalStorage()
    window.addEventListener('local-storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('local-storage', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return storedValue;
}

/**
 * Custom getThemeName function
 *
 * At runtime, it gets the correct theme from the `document`
 *
 * It replys on `document` `data-attributes` as primary mechanism,
 * ans `sessionStoryage` as a backup mechanism.
 *
 * It works in conjunction with Custom setThemeName from `@lendi/ui`
 *
 * @deprecated Deprecated since 0.2.3. Use SC's Theme instead.
 */
var getThemeName = () => {
  // Check browser for runtime
  if (isBrowser) {
    var _window, _process;
    // Get `theme` from DOM `data-attributes` if it's already available.
    var dataTheme = (_window = window) === null || _window === void 0 ? void 0 : _window.document.documentElement.getAttribute(themeAttribute);
    if (dataTheme) return dataTheme;

    // If not available, fallback to session storage (backwards compatibility with older LUIProvider)
    try {
      var storedTheme = window.sessionStorage.getItem('theme');
      if (storedTheme) return JSON.parse(storedTheme).value;
    } catch (error) {
      // TODO: Report Error
    }

    // conditionally generate warning
    var isRuntime = typeof process === 'undefined';
    var showWarning = isRuntime ? true : ((_process = process) === null || _process === void 0 || (_process = _process.env) === null || _process === void 0 ? void 0 : _process.NODE_ENV) !== 'test';
    if (showWarning) {
      console.warn("~~~LUI: No theme detected. Applying default theme \"".concat(defaultTheme, "\"~~~"), '\nYou may not have properly installed the LUIProvider - please refer to https://lendi.style/?path=/docs/foundations-luiprovider--interactive.\n');
    }
  }
  return defaultTheme;
};
var hasWindow = typeof window !== 'undefined';
var hasDocument = typeof document !== 'undefined';
var isBrowser = hasWindow && hasDocument;

// deriveSize based on 16px
var spacing = {
  // start deprecated values
  /*
   * @deprecated use '8' instead
   */
  xxs: 8,
  /*
   * @deprecated use '12' instead
   */
  xs: 12,
  /*
   * @deprecated use '16' instead
   */
  sm: 16,
  /*
   * @deprecated use '24' instead
   */
  md: 24,
  /*
   * @deprecated use '32' instead
   */
  lg: 32,
  /*
   * @deprecated use '40' instead
   */
  xl: 40,
  /*
   * @deprecated use '64' instead
   */
  xxl: 64,
  /*
   * @deprecated use '80' instead
   */
  xxxl: 80,
  /*
   * @deprecated
   */
  '6': 6,
  /*
   * @deprecated
   */
  '40': 40,
  /*
   * @deprecated
   */
  '56': 56,
  /*
   * @deprecated use '96' instead
   */
  '80': 80,
  // end deprecated values
  '0': 0,
  '2': 2,
  '4': 4,
  '8': 8,
  '12': 12,
  '16': 16,
  '24': 24,
  '32': 32,
  '48': 48,
  '64': 64,
  '96': 96,
  '128': 128
};

/**
 *
 * @param {number} size - pixel value
 * @returns {string} - result with rem unit
 *
 * @todo - remove baseFontSize cosnt and replace with token when available
 */

var pxToRem = size => {
  var baseFontSize = 16;
  return "".concat(size / baseFontSize, "rem");
};
var srOnly = css(["border:0 !important;clip:rect(1px,1px,1px,1px) !important;clip-path:inset(50%) !important;height:1px !important;overflow:hidden !important;padding:0 !important;position:absolute !important;width:1px !important;white-space:nowrap !important;"]);
var srOnlyFocusable = css(["&:focus,&:active{clip:auto !important;clip-path:none !important;height:auto !important;overflow:visible !important;width:auto !important;white-space:normal !important;}"]);
var defaultTheme = 'lendi';
var themeAttribute = 'data-lui-theme';
var getCDNLink = () => 'https://lendicdn.net/62182bb1683f25004d5254ce6e3461c3/71ccb7a35a452ea8153b6d920f9f190e';
var ThemeNames = /*#__PURE__*/function (ThemeNames) {
  ThemeNames["Lendi"] = "lendi";
  ThemeNames["LendiNext"] = "lendi-next";
  ThemeNames["Aussie"] = "aussie";
  ThemeNames["Domain"] = "domain";
  return ThemeNames;
}({});
export { ThemeNames, cssReset, cssResetObj, defaultTheme, escapeRegExp, filterGlobalProps, getCDNLink, getThemeName, isBrowser, pxToRem, spacing, srOnly, srOnlyFocusable, themeAttribute, useLocalStorage, useReadLocalStorage };